<template>
    <div>
        <!-- Search Header -->
        <section class="bg-grey-lighten-4 border-b">
            <div class="m-container py-8">
                <h1 class="titel-h1 font-weight-bold mb-4">Suche</h1>
                <MTextField
                    v-model="searchInput"
                    placeholder="Was suchst du? z.B. Sofa, Esstisch, Regal..."
                    prepend-inner-icon="magnify"
                    variant="solo"
                    density="comfortable"
                    hide-details
                    single-line
                    bg-color="white"
                    class="search-field"
                    @keyup.enter="executeSearch"
                >
                    <template #append-inner>
                        <MBtn
                            color="primary"
                            size="small"
                            variant="flat"
                            class="text-none"
                            @click="executeSearch"
                        >
                            Suchen
                        </MBtn>
                    </template>
                </MTextField>
            </div>
        </section>

        <div class="m-container py-8">
            <!-- Skeleton Loading -->
            <template v-if="loading">
                <MSkeletonLoader type="text" max-width="300" class="mb-6" />
                <MSkeletonLoader
                    type="heading"
                    max-width="200"
                    class="mb-4"
                />
                <div class="m-row mb-8">
                    <div
                        v-for="n in 4"
                        :key="n"
                        class="m-col m-col-12 m-col-sm-6 m-col-md-4 m-col-lg-3"
                    >
                        <MSkeletonLoader type="card" />
                    </div>
                </div>
                <MSkeletonLoader
                    type="heading"
                    max-width="200"
                    class="mb-4"
                />
                <div class="m-row">
                    <div v-for="n in 8" :key="n" class="m-col m-col-6 m-col-sm-4 m-col-md-3">
                        <ProductCardSkeleton />
                    </div>
                </div>
            </template>

            <!-- Search performed -->
            <template v-else-if="searchQuery">
                <div class="mb-6">
                    <span class="text-body-2 text-medium-emphasis">
                        {{ totalResults }} Ergebnisse für
                    </span>
                    <span class="text-body-1 font-weight-bold"
                        >"{{ searchQuery }}"</span
                    >
                </div>

                <!-- Category Results -->
                <template v-if="categoryResults.length > 0">
                    <h2 class="titel-h2 font-weight-bold mb-4">Kategorien</h2>
                    <div class="m-row mb-8">
                        <div
                            v-for="cat in categoryResults"
                            :key="cat.id"
                            class="m-col m-col-12 m-col-sm-6 m-col-md-4 m-col-lg-3"
                        >
                            <MCard
                                hover
                                rounded="md"
                                :to="getCatPath(cat)"
                                class="h-100"
                            >
                                <MCardText>
                                    <div class="d-flex align-center mb-2">
                                        <MIcon
                                            color="primary"
                                            :size="20"
                                            icon="folder"
                                            class="mr-2"
                                        />
                                        <span
                                            v-if="cat.parent"
                                            class="text-caption text-medium-emphasis"
                                        >
                                            {{ cat.parent.name }} &rsaquo;
                                        </span>
                                    </div>
                                    <h3
                                        class="text-subtitle-1 font-weight-bold"
                                    >
                                        {{ cat.name }}
                                    </h3>
                                    <p
                                        v-if="cat.titleDescription"
                                        class="text-body-2 text-medium-emphasis mt-1 search-heading"
                                    >
                                        {{ cat.titleDescription }}
                                    </p>
                                    <div
                                        v-if="
                                            cat.children &&
                                            cat.children.length > 0
                                        "
                                        class="mt-3 d-flex flex-wrap ga-1"
                                    >
                                        <MChip
                                            v-for="child in cat.children.slice(
                                                0,
                                                4,
                                            )"
                                            :key="child.id"
                                            size="x-small"
                                            variant="tonal"
                                            color="primary"
                                            :to="getCatPath(child)"
                                        >
                                            {{ child.name }}
                                        </MChip>
                                        <MChip
                                            v-if="cat.children.length > 4"
                                            size="x-small"
                                            variant="outlined"
                                        >
                                            +{{ cat.children.length - 4 }}
                                        </MChip>
                                    </div>
                                </MCardText>
                            </MCard>
                        </div>
                    </div>
                </template>

                <!-- Product Results -->
                <template v-if="productResults.length > 0">
                    <h2 class="titel-h2 font-weight-bold mb-4">Produkte</h2>
                    <div class="m-row">
                        <div
                            v-for="product in productResults"
                            :key="product.id"
                            class="m-col m-col-6 m-col-sm-4 m-col-md-3"
                        >
                            <ProductCard :product="product" />
                        </div>
                    </div>

                    <InfiniteScroll
                        :loading="loadingMore"
                        :has-more="hasMore"
                        @load="loadMore"
                    >
                        WEITERE PRODUKTE LADEN
                        <span style="opacity: 0.8; font-weight: 400;">
                            ({{ productResults.length }} VON
                            {{ serverTotal.toLocaleString("de-DE") }})
                        </span>
                    </InfiniteScroll>
                </template>

                <!-- No Results -->
                <div
                    v-if="!loading && totalResults === 0"
                    class="text-center py-16"
                >
                    <MIcon :size="64" color="grey-lighten-2" icon="magnify-close" class="mb-4" />
                    <h2 class="titel-h2 text-medium-emphasis mb-2">
                        Keine Ergebnisse
                    </h2>
                    <p class="text-body-2 text-medium-emphasis mb-6">
                        Leider konnten wir nichts für "{{ searchQuery }}"
                        finden.
                    </p>
                    <div class="mb-6">
                        <p class="text-body-2 text-medium-emphasis mb-3">
                            Probiere es mit anderen Suchbegriffen:
                        </p>
                        <div class="d-flex flex-wrap justify-center ga-2">
                            <MChip
                                v-for="suggestion in suggestions"
                                :key="suggestion"
                                variant="outlined"
                                color="primary"
                                rounded="pill"
                                @click="
                                    searchInput = suggestion;
                                    executeSearch();
                                "
                            >
                                {{ suggestion }}
                            </MChip>
                        </div>
                    </div>
                    <MBtn
                        variant="outlined"
                        color="primary"
                        to="/kategorien"
                        class="text-none"
                    >
                        Alle Kategorien ansehen
                    </MBtn>
                </div>
            </template>

            <!-- Initial State (no search yet) -->
            <div v-else-if="!loading" class="text-center py-16">
                <MIcon :size="64" color="grey-lighten-2" icon="magnify" class="mb-4" />
                <h2 class="titel-h2 text-medium-emphasis mb-2">
                    Was suchst du?
                </h2>
                <p class="text-body-2 text-medium-emphasis mb-6">
                    Gib einen Suchbegriff ein, um Möbel zu finden.
                </p>
                <div class="d-flex flex-wrap justify-center ga-2">
                    <MChip
                        v-for="suggestion in suggestions"
                        :key="suggestion"
                        variant="outlined"
                        color="primary"
                        rounded="pill"
                        @click="
                            searchInput = suggestion;
                            executeSearch();
                        "
                    >
                        {{ suggestion }}
                    </MChip>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import { getProductUrl } from "~/composables/useProductUrl";

const api = useApi();
const route = useRoute();
const router = useRouter();

const searchInput = ref("");
const searchQuery = ref("");
const loading = ref(false);
const loadingMore = ref(false);
const hasMore = ref(false);
const currentOffset = ref(0);
const PAGE_SIZE = 24;
const categoryResults = ref<any[]>([]);
const productResults = ref<any[]>([]);
const getCatPath = (cat: any) => {
    if (cat.parent?.slug) {
        if (cat.parent.parent?.slug) {
            return `/${cat.parent.parent.slug}/${cat.parent.slug}/${cat.slug}`;
        }
        return `/${cat.parent.slug}/${cat.slug}`;
    }
    return `/${cat.slug}`;
};

const suggestions = [
    "Sofa",
    "Esstisch",
    "Bett",
    "Schreibtisch",
    "Regal",
    "Schrank",
    "Lampe",
    "Kommode",
];

const serverTotal = ref(0);
const totalResults = computed(
    () => serverTotal.value || (categoryResults.value.length + productResults.value.length),
);

const fetchProducts = async (q: string, offset: number) => {
    const response = await api<any>("/api/search", {
        query: { q, offset, limit: PAGE_SIZE },
    });
    return response;
};

const executeSearch = async () => {
    const q = searchInput.value.trim();
    if (!q) return;

    router.replace({ query: { q } });
    searchQuery.value = q;
    loading.value = true;
    currentOffset.value = 0;

    try {
        const response = await fetchProducts(q, 0);
        categoryResults.value = response.categories || [];
        productResults.value = response.products || [];
        serverTotal.value = response.total ?? 0;
        hasMore.value = response.hasMore ?? false;
        currentOffset.value = response.products?.length ?? 0;
    } catch {
        categoryResults.value = [];
        productResults.value = [];
        hasMore.value = false;
    }

    loading.value = false;
};

const loadMore = async () => {
    if (loadingMore.value || !hasMore.value || !searchQuery.value) return;
    loadingMore.value = true;

    try {
        const response = await fetchProducts(
            searchQuery.value,
            currentOffset.value,
        );
        productResults.value.push(...(response.products || []));
        hasMore.value = response.hasMore ?? false;
        currentOffset.value += response.products?.length ?? 0;
    } catch {
        // noop
    }

    loadingMore.value = false;
};

// Load from query param on mount
onMounted(() => {
    const q = route.query.q as string;
    if (q) {
        searchInput.value = q;
        executeSearch();
    }
});

// React to query param changes (e.g. from header search) — debounced gegen Back/Forward-Spam
const debouncedExecuteSearch = useDebounceFn(executeSearch, 250);
watch(
    () => route.query.q,
    (newQ) => {
        if (newQ && newQ !== searchQuery.value) {
            searchInput.value = newQ as string;
            debouncedExecuteSearch();
        }
    },
);

useSeoMeta({
    title: computed(() =>
        searchQuery.value ? `"${searchQuery.value}" — Suche` : "Suche",
    ),
    description: computed(() =>
        searchQuery.value
            ? `Suchergebnisse für "${searchQuery.value}" — Möbel Preisvergleich bei Moebix.de`
            : "Durchsuche tausende Möbel aus hunderten Online-Shops bei Moebix.de",
    ),
    robots: "noindex, follow",
});

useHead({
    link: computed(() => {
        const q = (route.query.q as string) || searchQuery.value;
        if (!q) return [];
        return [{ rel: "canonical", href: `https://www.moebix.de/suche?q=${encodeURIComponent(q)}` }];
    }),
});
</script>

<style scoped>
.search-field {
    max-width: 700px;
}

.search-heading {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.border-b {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
